import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import React from 'react';
import { createContext, ReactNode, useEffect, useMemo, useRef } from 'react';
import { getAccessToken } from '../lib/azure_ad';
import { OVERRIDE_DEPARTMENT_HEADER } from '../lib/constants';
import { HttpClient } from '../lib/httpClient';
import { useUserConfiguration } from './UserConfigurationProvider';

interface HttpClientContextValues {
  httpClient: HttpClient | undefined;
}

const HttpClientContext = createContext<HttpClientContextValues>({
  httpClient: undefined,
});

interface HttpClientProviderProps {
  children: ReactNode;
}

export function HttpClientProvider({ children }: HttpClientProviderProps) {
  const { instance } = useMsal();
  const { department } = useUserConfiguration();

  // Keep latest auth values in a ref so the interceptor always reads the
  // current values without the axios instance or HttpClient being recreated.
  const authRef = useRef({ instance, department });
  useEffect(() => {
    authRef.current = { instance, department };
  }, [instance, department]);

  const httpClient = useMemo(() => {
    const axiosInstance = axios.create({});
    axiosInstance.interceptors.request.use(async (config) => {
      const { instance: msalInstance, department: dept } = authRef.current;
      const accessToken = await getAccessToken(msalInstance);

      if (accessToken != null) {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
        if (dept.override) {
          config.headers.set(OVERRIDE_DEPARTMENT_HEADER, dept.id);
        }
      }

      return config;
    });
    return new HttpClient(axiosInstance);
  }, []); // created once; interceptor reads latest values via authRef

  return (
    <HttpClientContext.Provider value={{ httpClient }}>
      {children}
    </HttpClientContext.Provider>
  );
}

export function useHttpClient() {
  const context = React.useContext(HttpClientContext);
  if (context === undefined) {
    throw new Error('useHttpClient must be used within a HttpClientProvider');
  }

  return context as {
    httpClient: HttpClient;
  };
}
